import { Divider, notification, Spin } from 'antd/lib'
import styles from './Result.module.less'
import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import { InlineLoader } from 'Components/common/InlineLoader/InlineLoader'
import HeaderComp from 'Components/common/Header/Header'
import { PAGE_TITLES } from 'Constants/page.constants'
import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import { useGetAttempt } from 'Hooks/exams/use-get-attempt'
import { Loader } from 'Components/common/Loader/Loader'
import { usePolling } from 'Hooks/use-polling'
import { ExamResult } from 'Types/Exams.types'
import { useTitle } from 'Hooks/use-title'

export const ResultPage = () => {
    useTitle(PAGE_TITLES.RESULT)

    const navigate = useNavigate()
    const params = useParams()
    const {
        data: attemptData,
        error,
        isPolling,
        stop,
    } = usePolling({
        hook: { fetchHook: useGetAttempt, params: params.attemptId },
        options: { interval: 5000, duration: 30000 },
    })

    const result = useMemo(() => {
        // added undefined check bcz on first render showExamResult will be undefined
        const showExamResult = attemptData?.attempt?.exam?.show_result
        if (showExamResult === undefined) return null
        return showExamResult ? attemptData?.attempt.result : ExamResult.SUBMITTED
    }, [attemptData?.attempt?.exam?.show_result, attemptData?.attempt.result])

    useEffect(() => {
        if (isPolling && result) {
            stop()
        }
    }, [result, isPolling, stop])

    useEffect(() => {
        if (error) {
            notification.error({
                message: (error as Error).message ?? 'Error in loading attempt',
                description:
                    'Please try again. If you still face the issue, reach out to the project owner',
                placement: 'bottomLeft',
            })
        }
    }, [error])

    if (!attemptData) {
        return <Spin indicator={<Loader title={'Loading result...'} />} />
    }

    const isManualEvaluation = false

    const isResultGenerationTakingTime = !!attemptData && !isPolling && !result
    const { attempts_remaining: attempts, name: examName, id: examId } = attemptData.attempt.exam

    return (
        <div className={styles['result-page']} data-testid="result-wrapper">
            <HeaderComp />
            <div className={styles['result-container']}>
                <div className={styles['result-box']}>
                    <div className={styles['result-title-container']}>
                        <h1 className={styles['result-title']}>Thanks for the submission</h1>
                        {isManualEvaluation && (
                            <p className={styles['result-title-info']}>
                                We will evaluate your answer and get back to you soon
                            </p>
                        )}
                    </div>
                    <Divider />
                    <div className={styles['result-subtitle-container']}>
                        <h2 className={styles['result-subtitle']}>{examName}</h2>
                        <p className={styles['result-subtitle-info']}>ID: {examId}</p>
                    </div>
                    {!isPolling && result ? (
                        <div className={styles['result-card-container']}>
                            {!isManualEvaluation ? (
                                <div className={styles['result-cards']}>
                                    <div className={styles['result-card']}>
                                        <p className={styles['result-card-title']}>Exam result</p>
                                        <p
                                            className={`${styles['result-card-info']} ${
                                                [ExamResult.PASS, ExamResult.SUBMITTED].includes(
                                                    result
                                                )
                                                    ? styles['pass']
                                                    : styles['fail']
                                            }`}
                                        >
                                            {result}
                                        </p>
                                        {[ExamResult.PASS, ExamResult.SUBMITTED].includes(
                                            result
                                        ) ? (
                                            <CheckCircleFilled className={styles['pass-icon']} />
                                        ) : (
                                            <CloseCircleFilled className={styles['fail-icon']} />
                                        )}
                                    </div>
                                    <div className={styles['result-card']}>
                                        <p className={styles['result-card-title']}>
                                            Attempts remaining
                                        </p>
                                        <p className={styles['result-card-info']}>{attempts}</p>
                                    </div>
                                </div>
                            ) : (
                                <h3 className={styles['manual-attempts-remaining']}>
                                    Attempts remaining: {attempts}
                                </h3>
                            )}
                            {attempts > 0 && (
                                <ActionButton
                                    onClick={() => {
                                        navigate(`/exam/${examId}`)
                                    }}
                                    style={{ marginLeft: '0' }}
                                    title="Retake Exam"
                                />
                            )}
                            <ActionButton
                                onClick={() => {
                                    navigate('/')
                                }}
                                color="default"
                                title="Go to Home"
                            />
                        </div>
                    ) : (
                        <div className={styles['result-loader']}>
                            <h3>Generating result...</h3>
                            <p>
                                It will take approximately 30 seconds to show the result. Later, you
                                can go to history tab also, to check your result.
                            </p>
                            {isResultGenerationTakingTime ? (
                                <ActionButton
                                    onClick={() => {
                                        navigate('/')
                                    }}
                                    color="default"
                                    title="Go to Home"
                                />
                            ) : (
                                <InlineLoader />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
